import React, { useCallback, useMemo } from "react";
import { Box, Card, CardContent, Chip, Link, Typography } from "@mui/material";
import { DataTable } from "../../components/DataTable/DataTable";
import { useCollection } from "../../hooks/useCollection";
import { getLeads } from "../../services/dataService";
import { dayTimeLabel, dialable } from "../../utils/queuePresentation";
import { tokens } from "../../utils/figmaTokens";

// The website sends the COURSE_SUBJECTS key, not the label, so the two stay in
// step even if the site renames a course. Keep in sync with
// 121islam-website/src/app/data/courses.ts.
const COURSE_LABELS = {
  nobleQuran: "The Noble Quran",
  arabicLanguage: "Arabic Language",
  islamicStudies: "Islamic Studies",
  notSure: "Not sure yet",
};

const asDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

const when = (v) => {
  const label = dayTimeLabel(v);
  return label === "-" ? "—" : label;
};

/** A lead is "new" for its first 48 hours — that is the window worth calling. */
const isNew = (v) => {
  const d = asDate(v);
  return d && !isNaN(d) && Date.now() - d.getTime() < 48 * 60 * 60 * 1000;
};

// Read-only by rule (spec 026 D7: `allow update, delete: if false` on
// `leads`), so DataTable renders these columns with no row actions.
const columns = [
  {
    key: "name",
    label: "Name",
    sortable: true,
    render: (row) => (
      <>
        {row.name}
        {isNew(row.createdAt) && (
          <Chip label="New" size="small" color="success" sx={{ ml: 1 }} />
        )}
      </>
    ),
  },
  {
    key: "phone",
    label: "Phone",
    render: (row) => <Link href={`tel:${dialable(row.phone)}`}>{row.phone}</Link>,
  },
  {
    key: "course",
    label: "Course",
    sortable: true,
    render: (row) => COURSE_LABELS[row.course] || row.course || "—",
  },
  {
    key: "source",
    label: "Source",
    render: (row) => row.source || "—",
  },
  {
    key: "createdAt",
    label: "Received",
    render: (row) => when(row.createdAt),
  },
];

const Leads = () => {
  // Memoized: an inline fetcher is a new identity every render and would
  // make useCollection's effect refetch forever. getLeads() itself orders by
  // createdAt desc, so the fetched order is already newest-first.
  const fetcher = useCallback(() => getLeads(), []);
  const { data, loading, error } = useCollection(fetcher);

  const newCount = useMemo(
    () => data.filter((l) => isNew(l.createdAt)).length,
    [data]
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
          Leads
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Free-trial requests from the ads landing page. Call them back as soon
          as you can.
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: "flex", gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total leads
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {data.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              New (last 48h)
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: tokens.success.ink }}>
              {newCount}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        rows={data}
        searchKeys={["name", "phone", "course"]}
        loading={loading}
        error={error}
        emptyText="No leads yet."
      />
    </Box>
  );
};

export default Leads;
